import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import DataGrid, { Column, Paging, Scrolling } from "devextreme-react/data-grid";
import { formatNumber } from "devextreme/localization";
import { FaEye } from "react-icons/fa";

interface DuplicateOffersModalProps {
  show: boolean;
  onClose: () => void;
  message: string;
  duplicateData: any[];
  onConfirm?: () => void;
}

const DuplicateOffersModal: React.FC<DuplicateOffersModalProps> = ({
  show,
  onClose,
  message,
  duplicateData = [],
  onConfirm,
}) => {
  const [viewModal, setViewModal] = useState<{
    type: string;
    list: string[];
  } | null>(null);

  const handleViewList = (type: string, list: string[]) => {
    setViewModal({ type, list });
  };

  const closeViewModal = () => {
    setViewModal(null);
  };

  const renderEyeIcon = (type: string, list: string[]) => {
    if (!list || list.length === 0) return <span className="text-muted">—</span>;
    return (
      <button
        type="button"
        className="btn btn-link p-0"
        onClick={() => handleViewList(type, list)}
        title={`View ${type}`}
        style={{ color: 'black' }}
      >
        <FaEye />
      </button>
    );
  };

  return (
    <>
      {/* Main Duplicate Modal */}
      <Modal
        show={show}
        onHide={onClose}
        centered
        size="xl"
        backdrop="static"
        keyboard={false}
        style={{ zIndex: 1055 }}
      >
        <Modal.Header closeButton>
          <h5 className="mb-0">Duplicate Offers Found</h5>
        </Modal.Header>

        <Modal.Body
          style={{
            maxHeight: "80vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {message && (
            <div className="alert alert-warning fw-medium mb-3" style={{ fontSize: "0.95rem" }}>
              {message}
            </div>
          )}

          <div
            style={{
              flex: 1,
              overflow: "auto",
              border: "1px solid #e5e5e5",
              borderRadius: "6px",
            }}
          >
            <DataGrid
              dataSource={duplicateData}
              keyExpr="id"
              rowAlternationEnabled
              hoverStateEnabled
              allowColumnResizing
              columnAutoWidth
              height="100%"
            >
              <Scrolling mode="standard" useNative scrollByContent scrollByThumb />
              <Paging enabled defaultPageSize={20} />

              <Column dataField="storeName" caption="Store" width={200} />
              <Column dataField="promotionName" caption="Promotion" />
              <Column dataField="materialType" caption="Material Type" />
              <Column dataField="mrpType" caption="MRP Type" />
              <Column dataField="discountOn" caption="Discount On" />
              <Column dataField="discountType" caption="Discount Type" />
              <Column
                dataField="discountValue"
                caption="Discount Value"
                cellRender={({ data }) => {
                  if (data.discountValue == null) return "";
                  const formattedValue = formatNumber(data.discountValue, "#,##0.00");
                  return data.isPercentage ? `${formattedValue} %` : formattedValue;
                }}
              />
              <Column dataField="invoiceSlab" caption="Invoice Range" />
              <Column dataField="fromDate" caption="Valid From" dataType="date" format="dd/MM/yyyy" />
              <Column dataField="toDate" caption="Valid To" dataType="date" format="dd/MM/yyyy" />
              <Column
                dataField="isDefault"
                caption="Default Offer"
                cellRender={({ data }) => <span>{data.isDefault ? "Yes" : "No"}</span>}
              />
              <Column
                dataField="isClub"
                caption="Allow Offer Combination"
                cellRender={({ data }) => <span>{data.isClub ? "Yes" : "No"}</span>}
              />

              {/* Eye icon columns */}
              <Column
                caption="Categories"
                cellRender={({ data }) => renderEyeIcon("Categories", data.categoryList)}
              />
              <Column
                caption="Collections"
                cellRender={({ data }) => renderEyeIcon("Collections", data.collectionList)}
              />
              <Column
                caption="Styles"
                cellRender={({ data }) => renderEyeIcon("Styles", data.styleList)}
              />
              <Column
                caption="Karats"
                cellRender={({ data }) => renderEyeIcon("Karats", data.karatList)}
              />
            </DataGrid>
          </div>
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-end">
          <button className="btn add-btn" onClick={onClose}>
            Close
          </button>
          {onConfirm && (
            <button className="btn submit-btn" onClick={onConfirm}>
              Continue Anyway
            </button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Small Modal for Viewing Lists */}
      <Modal show={!!viewModal} onHide={closeViewModal} centered size="sm">
        <Modal.Header closeButton>
          <h6 className="mb-0">{viewModal?.type}</h6>
        </Modal.Header>
        <Modal.Body
          style={{
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          {viewModal?.list && viewModal.list.length > 0 ? (
            <ul className="list-group">
              {viewModal.list.map((item, idx) => (
                <li key={idx} className="list-group-item py-1">
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted mb-0 text-center">No items found.</p>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DuplicateOffersModal;
